'use client';

import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';

const strategies = [
    {
        num: '01',
        title: 'On-Page SEO Optimization',
        body: 'We began by strengthening the site\'s SEO foundation. Product and category pages lacked optimized title tags, meta descriptions, and heading structures. Our team audited and optimized the highest-priority pages with relevant keyword targeting, improving search engine understanding and increasing visibility for important commercial terms.',
    },
    {
        num: '02',
        title: 'Product Content Enhancement',
        body: 'Many product pages contained duplicate manufacturer content or minimal descriptions that offered little value to users or search engines. We rewrote product descriptions to provide clear, informative, and search-friendly content — covering product benefits, key ingredients, use cases, and relevant health concerns while maintaining readability and trustworthiness.',
    },
    {
        num: '03',
        title: 'Content Marketing & Informational SEO',
        body: 'A significant opportunity existed around informational search queries that potential customers were actively searching for. We developed a content strategy targeting symptom-based and problem-solving keywords. Consistently publishing 6–8 high-quality articles each month helped expand keyword coverage and improve organic visibility. Several articles achieved first-page rankings, generating qualified traffic.',
    },
    {
        num: '04',
        title: 'Website Speed & Performance Optimization',
        body: 'Mobile performance was identified as a major area for improvement. By optimising images, reducing unnecessary page weight, and collaborating with the development team on technical enhancements, we improved site speed and user experience — contributing to better engagement metrics and enhanced search performance.',
    },
    {
        num: '05',
        title: 'Authority Building Through Strategic Link Acquisition',
        body: 'To strengthen the website\'s authority, we implemented a focused backlink acquisition strategy through guest contributions, industry-relevant health publications, and digital PR opportunities. We also increased brand visibility on Quora by contributing valuable, topic-relevant content that supported brand awareness and referral traffic.',
    },
    {
        num: '06',
        title: 'Backlink Profile Cleanup',
        body: 'A comprehensive backlink audit revealed several low-quality and potentially harmful links pointing to the website. We removed unnecessary risks by identifying and disavowing toxic backlinks, helping maintain a clean and healthy link profile aligned with search engine best practices.',
    },
    {
        num: '07',
        title: 'Internal Linking Optimization',
        body: 'The website lacked a structured internal linking framework, limiting the flow of authority between key pages. We created a strategic internal linking structure that connected blog content with product and category pages — improving crawlability, user navigation, and the distribution of ranking signals throughout the website.',
    },
];

export default function HealthcareEcommerceSEOPage() {
    const observerRefs = useRef<(HTMLElement | null)[]>([]);

    useEffect(() => {
        const obs = new IntersectionObserver((entries) => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    (e.target as HTMLElement).classList.add('hcs-visible');
                    obs.unobserve(e.target);
                }
            });
        }, { threshold: 0.08 });
        observerRefs.current.forEach(el => { if (el) obs.observe(el); });
        return () => obs.disconnect();
    }, []);

    const ref = (i: number) => (el: HTMLElement | null) => { observerRefs.current[i] = el; };

    return (
        <>
            <style>{`
                .hcs-fade { opacity: 0; transform: translateY(32px); transition: opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1); }
                .hcs-fade.hcs-visible { opacity: 1; transform: none; }
                .hcs-scale { opacity: 0; transform: scale(0.95); transition: opacity 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1); }
                .hcs-scale.hcs-visible { opacity: 1; transform: none; }
                .ga4-img { border-radius: 16px; box-shadow: 0 24px 80px rgba(0,0,0,0.45); transition: transform 0.5s ease, box-shadow 0.5s ease; }
                .ga4-img:hover { transform: translateY(-8px) scale(1.02); box-shadow: 0 40px 100px rgba(0,0,0,0.6); }
                .hcs-metric-card:hover { border-color: rgba(206,255,26,0.3) !important; background: rgba(206,255,26,0.05) !important; }
                .hcs-cta-btn:hover { transform: scale(1.05); }
                .hcs-back-link:hover { color: rgba(251,253,253,0.8) !important; }
                @media (max-width: 900px) {
                    .hcs-hero-grid, .hcs-outcome-grid { flex-direction: column !important; }
                    .hcs-metrics-row { flex-direction: column !important; }
                    .hcs-inner { padding: 40px 24px !important; }
                    .hcs-stat-row { flex-wrap: wrap !important; }
                    .hcs-container { padding-left: 20px !important; padding-right: 20px !important; }
                    .hcs-stats-col { min-width: 0 !important; width: 100% !important; }
                }
            `}</style>

            <main style={{ background: '#080400', minHeight: '100vh' }}>
                <div style={{ height: 100 }} />

                {/* ══════════════════════════════════
                    HERO
                ══════════════════════════════════ */}
                <section style={{ padding: '60px 0 0', position: 'relative', overflow: 'hidden' }}>
                    <div style={{
                        position: 'absolute', inset: 0, opacity: 0.025,
                        backgroundImage: 'linear-gradient(rgba(206,255,26,1) 1px, transparent 1px), linear-gradient(90deg, rgba(206,255,26,1) 1px, transparent 1px)',
                        backgroundSize: '48px 48px',
                    }} />

                    <div className="hcs-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px', position: 'relative', zIndex: 1 }}>

                        {/* Breadcrumb */}
                        <div ref={ref(0)} className="hcs-fade" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 52 }}>
                            <Link href="/our-work/case-studies" style={{ fontFamily: 'var(--font-manrope)', fontSize: 13, color: 'rgba(251,253,253,0.3)', textDecoration: 'none', letterSpacing: '0.06em' }}>Case Studies</Link>
                            <span style={{ color: 'rgba(251,253,253,0.15)', fontSize: 13 }}>›</span>
                            <span style={{ fontFamily: 'var(--font-manrope)', fontSize: 13, color: 'rgba(251,253,253,0.5)', letterSpacing: '0.06em' }}>Healthcare E-commerce SEO</span>
                        </div>

                        {/* Hero grid */}
                        <div className="hcs-hero-grid" style={{ display: 'flex', gap: 64, alignItems: 'center' }}>
                            {/* Left: text */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div ref={ref(1)} className="hcs-fade" style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                    background: 'rgba(206,255,26,0.07)', border: '1px solid rgba(206,255,26,0.2)',
                                    borderRadius: 50, padding: '6px 18px', marginBottom: 28,
                                }}>
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ceff1a', display: 'inline-block' }} />
                                    <span style={{ fontFamily: 'var(--font-manrope)', fontWeight: 700, fontSize: 11, color: '#ceff1a', letterSpacing: '0.18em', textTransform: 'uppercase' }}>Healthcare & Wellness · SEO</span>
                                </div>

                                <h1 ref={ref(2)} className="hcs-fade" style={{
                                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                    fontWeight: 700, fontSize: 'clamp(36px, 5vw, 68px)',
                                    color: '#fbfdfd', letterSpacing: '0.03em', lineHeight: 1.08, margin: '0 0 28px',
                                }}>
                                    Healthcare<br />E-commerce:<br />
                                    <span style={{ color: '#ceff1a' }}>249% Growth</span><br />in 5 Months
                                </h1>

                                <p ref={ref(3)} className="hcs-fade" style={{
                                    fontFamily: 'var(--font-manrope)', fontSize: 'clamp(15px, 1.3vw, 18px)',
                                    color: 'rgba(251,253,253,0.45)', lineHeight: 1.85, letterSpacing: '0.02em',
                                    maxWidth: 520, margin: '0 0 48px',
                                }}>
                                    From 22,897 to 80,082 monthly organic sessions — how a structured SEO strategy delivered 249% traffic growth, 176% more events, and 44% revenue uplift for a healthcare e-commerce brand in just five months.
                                </p>

                                {/* Stat chips */}
                                <div ref={ref(4)} className="hcs-fade hcs-stat-row" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                    {[
                                        { val: '+249%', lbl: 'Organic Sessions' },
                                        { val: '+176%', lbl: 'Event Count' },
                                        { val: '+44%', lbl: 'Revenue' },
                                        { val: '5 Mo.', lbl: 'Timeline' },
                                    ].map(s => (
                                        <div key={s.val} style={{
                                            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                                            borderRadius: 14, padding: '14px 22px', textAlign: 'center',
                                        }}>
                                            <div style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(18px, 2.2vw, 28px)', color: '#ceff1a', letterSpacing: '-0.01em' }}>{s.val}</div>
                                            <div style={{ fontFamily: 'var(--font-manrope)', fontWeight: 500, fontSize: 11, color: 'rgba(251,253,253,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 4 }}>{s.lbl}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Right: cover image */}
                            <div ref={ref(5)} className="hcs-scale" style={{ flexShrink: 0, width: 'clamp(260px, 36vw, 520px)' }}>
                                <Image src="/images/our-work/healthcare-seo-cover.webp" alt="Healthcare E-commerce SEO"
                                    width={520} height={380} style={{ width: '100%', height: 'auto', borderRadius: 28, display: 'block', boxShadow: '0 40px 120px rgba(0,0,0,0.6)' }} />
                            </div>
                        </div>
                    </div>

                    <div style={{ height: 100, background: 'linear-gradient(transparent, #080400)', marginTop: 64 }} />
                </section>

                {/* ══════════════════════════════════
                    BEFORE vs AFTER METRICS
                ══════════════════════════════════ */}
                <section className="hcs-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px 100px' }}>
                    <div ref={ref(6)} className="hcs-fade" style={{ marginBottom: 48 }}>
                        <p style={{ fontFamily: 'var(--font-manrope)', fontWeight: 800, fontSize: 11, color: 'rgba(251,253,253,0.25)', letterSpacing: '0.22em', textTransform: 'uppercase', margin: '0 0 14px' }}>Initial vs Final Metrics</p>
                        <h2 style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(26px, 3.5vw, 48px)', color: '#fbfdfd', letterSpacing: '0.03em', margin: 0, lineHeight: 1.15 }}>
                            Results Achieved in 5 Months
                        </h2>
                    </div>

                    {/* 3 metric cards */}
                    <div className="hcs-metrics-row" style={{ display: 'flex', gap: 20, marginBottom: 64 }}>
                        {[
                            {
                                label: 'Organic Sessions',
                                before: '22,897', after: '80,082',
                                change: '+249.75%',
                                period: 'Apr–May 2025 → Jun–Oct 2025',
                                color: '#ceff1a',
                            },
                            {
                                label: 'Event Count',
                                before: '1,65,518', after: '4,56,880',
                                change: '+176.03%',
                                period: 'Apr–May 2025 → Jun–Oct 2025',
                                color: '#ceff1a',
                            },
                            {
                                label: 'Organic Revenue',
                                before: '₹1,33,665', after: '₹1,92,937',
                                change: '+44.34%',
                                period: 'Apr–May 2025 → Jun–Oct 2025',
                                color: '#ceff1a',
                            },
                        ].map((m, i) => (
                            <div
                                key={m.label}
                                ref={ref(7 + i)}
                                className="hcs-fade hcs-metric-card"
                                style={{
                                    flex: 1, minWidth: 0,
                                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: 24, padding: '36px 32px',
                                    transition: 'border-color 0.3s, background 0.3s',
                                    transitionDelay: `${i * 0.1}s`,
                                }}
                            >
                                <p style={{ fontFamily: 'var(--font-manrope)', fontWeight: 700, fontSize: 11, color: 'rgba(251,253,253,0.3)', letterSpacing: '0.16em', textTransform: 'uppercase', margin: '0 0 20px' }}>{m.label}</p>

                                {/* Before */}
                                <div style={{ marginBottom: 12 }}>
                                    <div style={{ fontFamily: 'var(--font-manrope)', fontSize: 11, color: 'rgba(251,253,253,0.2)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>Before</div>
                                    <div style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 28, color: 'rgba(251,253,253,0.25)', lineHeight: 1 }}>{m.before}</div>
                                </div>

                                {/* Arrow */}
                                <div style={{ marginBottom: 12 }}>
                                    <svg width="20" height="14" viewBox="0 0 20 14" fill="none">
                                        <path d="M2 7h16M12 2l6 5-6 5" stroke="rgba(206,255,26,0.4)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>

                                {/* After */}
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ fontFamily: 'var(--font-manrope)', fontSize: 11, color: 'rgba(251,253,253,0.2)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>After</div>
                                    <div style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 36, color: '#fbfdfd', lineHeight: 1 }}>{m.after}</div>
                                </div>

                                {/* Change badge */}
                                <div style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    background: 'rgba(206,255,26,0.1)', border: '1px solid rgba(206,255,26,0.2)',
                                    borderRadius: 50, padding: '5px 14px',
                                }}>
                                    <span style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 15, color: '#ceff1a' }}>{m.change}</span>
                                </div>

                                <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 11, color: 'rgba(251,253,253,0.15)', letterSpacing: '0.06em', margin: '12px 0 0' }}>{m.period}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ══════════════════════════════════
                    GA4 SCREENSHOT — THE PROOF
                ══════════════════════════════════ */}
                <section className="hcs-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px 120px' }}>
                    <div ref={ref(10)} className="hcs-fade hcs-inner" style={{
                        background: 'rgba(206,255,26,0.03)', border: '1.5px solid rgba(206,255,26,0.12)',
                        borderRadius: 32, padding: '56px', position: 'relative', overflow: 'hidden',
                    }}>
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(206,255,26,0.5), transparent)' }} />

                        <div style={{ marginBottom: 40 }}>
                            <p style={{ fontFamily: 'var(--font-manrope)', fontWeight: 800, fontSize: 11, color: '#ceff1a', letterSpacing: '0.22em', textTransform: 'uppercase', margin: '0 0 14px' }}>Verified GA4 Data</p>
                            <h2 style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(22px, 3vw, 42px)', color: '#fbfdfd', letterSpacing: '0.03em', margin: '0 0 12px', lineHeight: 1.2 }}>
                                Traffic Acquisition Report
                            </h2>
                            <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 15, color: 'rgba(251,253,253,0.35)', letterSpacing: '0.02em', margin: 0 }}>
                                Jun 1–Oct 31, 2025 vs Apr 1–May 31, 2025 · Organic Search channel
                            </p>
                        </div>

                        <div ref={ref(11)} className="hcs-scale">
                            <Image src="/images/case-studies/healthcare-seo/ga4-screenshot.webp"
                                alt="GA4 Traffic Acquisition Report — Healthcare SEO Growth"
                                width={1200} height={600} className="ga4-img" style={{ width: '100%', height: 'auto', display: 'block' }} />
                            <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 12, color: 'rgba(251,253,253,0.2)', letterSpacing: '0.08em', textAlign: 'center', marginTop: 18 }}>
                                Google Analytics 4 · Traffic Acquisition · Session primary channel group · Jun 1 – Oct 31, 2025
                            </p>
                        </div>
                    </div>
                </section>

                {/* ══════════════════════════════════
                    STRATEGIES
                ══════════════════════════════════ */}
                <section className="hcs-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px 120px' }}>
                    <div ref={ref(12)} className="hcs-fade" style={{ marginBottom: 72 }}>
                        <p style={{ fontFamily: 'var(--font-manrope)', fontWeight: 800, fontSize: 12, color: '#ceff1a', letterSpacing: '0.22em', textTransform: 'uppercase', margin: '0 0 18px' }}>How We Did It</p>
                        <h2 style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(30px, 4.5vw, 58px)', color: '#fbfdfd', letterSpacing: '0.03em', margin: 0, lineHeight: 1.1 }}>
                            7 Strategies.<br />One Breakthrough.
                        </h2>
                    </div>

                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        {strategies.map((s, i) => (
                            <div
                                key={s.num}
                                ref={ref(13 + i)}
                                className="hcs-fade"
                                style={{
                                    display: 'flex', gap: 'clamp(20px, 4vw, 80px)', alignItems: 'flex-start',
                                    padding: '44px 0',
                                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                                    transitionDelay: `${i * 0.06}s`,
                                }}
                            >
                                <div style={{
                                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                    fontWeight: 700, fontSize: 'clamp(44px, 5.5vw, 80px)',
                                    color: 'rgba(206,255,26,0.08)', lineHeight: 1, flexShrink: 0,
                                    minWidth: 'clamp(56px, 7.5vw, 104px)', letterSpacing: '-0.03em',
                                }}>{s.num}</div>
                                <div style={{ flex: 1, paddingTop: 8 }}>
                                    <h3 style={{
                                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                        fontWeight: 600, fontSize: 'clamp(17px, 1.8vw, 24px)',
                                        color: '#fbfdfd', letterSpacing: '0.03em', margin: '0 0 10px', lineHeight: 1.3,
                                    }}>{s.title}</h3>
                                    <p style={{
                                        fontFamily: 'var(--font-manrope)', fontSize: 'clamp(14px, 1.1vw, 16px)',
                                        color: 'rgba(251,253,253,0.4)', lineHeight: 1.9, letterSpacing: '0.02em', margin: 0,
                                    }}>{s.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ══════════════════════════════════
                    OUTCOME SUMMARY
                ══════════════════════════════════ */}
                <section className="hcs-container" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 48px 120px' }}>
                    <div ref={ref(20)} className="hcs-fade hcs-inner" style={{
                        background: 'rgba(206,255,26,0.03)', border: '1px solid rgba(206,255,26,0.1)',
                        borderRadius: 32, padding: '64px',
                    }}>
                        <div className="hcs-outcome-grid" style={{ display: 'flex', gap: 72, alignItems: 'flex-start' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontFamily: 'var(--font-manrope)', fontWeight: 800, fontSize: 11, color: '#ceff1a', letterSpacing: '0.22em', textTransform: 'uppercase', margin: '0 0 16px' }}>The Outcome</p>
                                <h2 style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(24px, 3vw, 42px)', color: '#fbfdfd', letterSpacing: '0.03em', margin: '0 0 20px', lineHeight: 1.2 }}>
                                    Sustainable Growth.<br />Long-Term Results.
                                </h2>
                                <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 16, color: 'rgba(251,253,253,0.4)', lineHeight: 1.9, margin: 0, letterSpacing: '0.02em' }}>
                                    The growth achieved was the result of a comprehensive SEO strategy rather than a single tactic. By combining technical improvements, content optimization, authority building, and user experience enhancements, the brand significantly increased its organic visibility, engagement, and revenue.
                                </p>
                                <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 15, color: 'rgba(251,253,253,0.3)', lineHeight: 1.85, margin: '16px 0 0', letterSpacing: '0.02em' }}>
                                    Operating within the healthcare industry required a careful approach to content quality and accuracy. Every optimization was designed to align with user intent while maintaining credibility, relevance, and compliance with industry best practices.
                                </p>
                            </div>

                            <div className="hcs-stats-col" style={{ display: 'flex', flexDirection: 'column', gap: 22, flexShrink: 0, minWidth: 300 }}>
                                {[
                                    { label: 'Organic Sessions', before: '22,897', after: '80,082' },
                                    { label: 'Event Count', before: '1,65,518', after: '4,56,880' },
                                    { label: 'Organic Revenue', before: '₹1,33,665', after: '₹1,92,937' },
                                    { label: 'Total Sessions Growth', before: '50,486', after: '2,18,849' },
                                ].map(m => (
                                    <div key={m.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
                                        <div style={{ fontFamily: 'var(--font-manrope)', fontSize: 13, color: 'rgba(251,253,253,0.3)', letterSpacing: '0.05em', minWidth: 120 }}>{m.label}</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <span style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 600, fontSize: 14, color: 'rgba(251,253,253,0.18)' }}>{m.before}</span>
                                            <svg width="16" height="10" viewBox="0 0 16 10" fill="none">
                                                <path d="M1 5h14M10 1l4 4-4 4" stroke="rgba(206,255,26,0.45)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <span style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 17, color: '#ceff1a' }}>{m.after}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ══════════════════════════════════
                    CTA
                ══════════════════════════════════ */}
                <section style={{ textAlign: 'center', padding: '0 24px 140px' }}>
                    <div ref={ref(21)} className="hcs-fade" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
                        <h2 style={{
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            fontWeight: 600, fontSize: 'clamp(22px, 3.5vw, 46px)',
                            color: '#fbfdfd', letterSpacing: '0.03em', margin: 0,
                        }}>Ready to grow your organic traffic?</h2>
                        <p style={{ fontFamily: 'var(--font-manrope)', fontSize: 17, color: 'rgba(251,253,253,0.35)', margin: 0, maxWidth: 460, lineHeight: 1.75, letterSpacing: '0.02em' }}>
                            Let's build an SEO strategy that delivers real, measurable results for your brand.
                        </p>
                        <Link href="/contact" className="hcs-cta-btn" style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            background: '#ceff1a', color: '#080400',
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            fontWeight: 600, fontSize: 18, letterSpacing: '0.06em',
                            padding: '18px 52px', borderRadius: 50, textDecoration: 'none',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        }}>
                            Book a Free SEO Audit →
                        </Link>
                        <Link href="/our-work/case-studies" className="hcs-back-link" style={{
                            fontFamily: 'var(--font-manrope)', fontWeight: 500, fontSize: 14,
                            color: 'rgba(251,253,253,0.25)', textDecoration: 'none', letterSpacing: '0.05em',
                            transition: 'color 0.2s',
                        }}>
                            ← Back to Case Studies
                        </Link>
                    </div>
                </section>
            </main>
        </>
    );
}
